import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Plus, Pencil, Trash2, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const CATS = ["Bolsos", "Ropa", "Accesorios"];

const emptyForm = {
  name: "",
  description: "",
  price: "",
  category: "Bolsos",
  image_url: "",
  extra_images_text: "",
  materials: "",
  measurements: "",
  in_stock: true,
  featured: false,
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchProducts = () => {
    api.get("/products").then((res) => setProducts(res.data));
  };

  useEffect(() => {
    if (user) fetchProducts();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const openNew = () => {
    setForm(emptyForm);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      description: p.description || "",
      price: String(p.price),
      category: p.category,
      image_url: p.image_url,
      extra_images_text: (p.extra_images || []).join("\n"),
      materials: p.materials || "",
      measurements: p.measurements || "",
      in_stock: p.in_stock,
      featured: p.featured,
    });
    setEditId(p.id);
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const extraImgs = form.extra_images_text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      name: form.name,
      description: form.description,
      category: form.category,
      image_url: form.image_url,
      extra_images: extraImgs,
      materials: form.materials,
      measurements: form.measurements,
      in_stock: form.in_stock,
      featured: form.featured,
      price: parseFloat(form.price),
    };
    try {
      if (editId) {
        await api.put(`/products/${editId}`, payload);
        toast.success("Pieza actualizada");
      } else {
        await api.post("/products", payload);
        toast.success("Pieza creada");
      }
      setOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Error al guardar"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta pieza?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Pieza eliminada");
      fetchProducts();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="label-tracked text-[#C27A62] mb-2">Panel</div>
          <h1 className="font-serif-editorial text-4xl md:text-5xl">Mis piezas</h1>
          <p className="text-sm text-[#5C5C5C] mt-2">{user.email}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={openNew} className="btn-primary" data-testid="new-product-btn">
            <Plus size={16} /> Añadir pieza
          </button>
          <button onClick={logout} className="btn-secondary" data-testid="logout-btn">
            <LogOut size={14} /> Salir
          </button>
        </div>
      </div>

      <div className="bg-[#FAF9F5] border border-[#E5E0D8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5E0D8]">
            <tr className="text-left">
              <th className="p-4 label-tracked"></th>
              <th className="p-4 label-tracked">Nombre</th>
              <th className="p-4 label-tracked">Categoría</th>
              <th className="p-4 label-tracked">Precio</th>
              <th className="p-4 label-tracked">Stock</th>
              <th className="p-4 label-tracked">Destacada</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-[#E5E0D8]" data-testid={`row-${p.id}`}>
                <td className="p-4 w-20">
                  <div className="w-14 h-16 bg-[#EDE8DD] overflow-hidden">
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                </td>
                <td className="p-4 font-serif-editorial text-lg">{p.name}</td>
                <td className="p-4 text-[#5C5C5C]">{p.category}</td>
                <td className="p-4">€{p.price.toFixed(2)}</td>
                <td className="p-4">{p.in_stock ? "Sí" : "No"}</td>
                <td className="p-4">{p.featured ? "★" : "—"}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEdit(p)}
                    className="p-2 hover:text-[#C27A62]"
                    data-testid={`edit-${p.id}`}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="p-2 hover:text-red-700"
                    data-testid={`delete-${p.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={7} className="p-10 text-center text-[#5C5C5C]">
                  Aún no hay piezas. Crea la primera.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF9F5] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-editorial text-2xl">
              {editId ? "Editar pieza" : "Nueva pieza"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="label-tracked block mb-1">Nombre</label>
              <input
                className="input-clean"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-tracked block mb-1">Categoría</label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="form-category" className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-tracked block mb-1">Precio (€)</label>
                <input
                  className="input-clean"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  required
                  data-testid="form-price"
                />
              </div>
            </div>
            <div>
              <label className="label-tracked block mb-1">URL imagen</label>
              <input
                className="input-clean"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                required
                data-testid="form-image-url"
              />
              {form.image_url && (
                <div className="mt-3 w-32 h-40 bg-[#EDE8DD] overflow-hidden">
                  <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div>
              <label className="label-tracked block mb-1">Imágenes extra (una URL por línea)</label>
              <textarea
                className="input-clean min-h-[70px] resize-none text-xs"
                value={form.extra_images_text}
                onChange={(e) => setForm({ ...form, extra_images_text: e.target.value })}
                placeholder="https://...img1.jpg&#10;https://...img2.jpg"
                data-testid="form-extra-images"
              />
            </div>
            <div>
              <label className="label-tracked block mb-1">Descripción</label>
              <textarea
                className="input-clean min-h-[100px] resize-none"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="form-description"
              />
            </div>
            <div>
              <label className="label-tracked block mb-1">Materiales</label>
              <input
                className="input-clean"
                value={form.materials}
                onChange={(e) => setForm({ ...form, materials: e.target.value })}
                data-testid="form-materials"
              />
            </div>
            <div>
              <label className="label-tracked block mb-1">Medidas</label>
              <input
                className="input-clean"
                value={form.measurements}
                onChange={(e) => setForm({ ...form, measurements: e.target.value })}
                placeholder="ej: 45 × 35 × 10 cm"
                data-testid="form-measurements"
              />
            </div>
            <div className="flex gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.in_stock}
                  onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                  data-testid="form-in-stock"
                />
                En stock
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="form-featured"
                />
                Destacada
              </label>
            </div>
            <DialogFooter className="pt-4">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-secondary"
                data-testid="form-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
                data-testid="form-submit"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
